import { produce } from "immer";
import { find } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Line } from "react-konva";

import { getHotspotCenter, getScaledCoordinates } from "@examspark/common-util";
import { Stem, StemGraphicAssociate } from "@examspark/types";

import QuestionContext from "../QuestionContext";

import { styles } from "./common/image-map/Hotspot";
import ImageMap from "./common/image-map/ImageMap";
import Prompt from "./shared/Prompt";

interface Props {
  data: StemGraphicAssociate;
}

const GraphicAssociate: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const [selectedSpot, setSelectedSpot] = useState<Stem.Spot>();

  const handleSpotClick = (spot: Stem.Spot) => {
    if (selectedSpot && selectedSpot.id !== spot.id) {
      const rsp = produce(
        player?.response?.[props.data.id]?.pairs || [],
        (draft: any) => {
          const exists = draft.some((p: any) => {
            return p.includes(selectedSpot.id) && p.includes(spot.id);
          });

          if (exists) {
            return draft.filter(
              (p: any) => !(p.includes(selectedSpot.id) && p.includes(spot.id)),
            );
          } else {
            draft.push([selectedSpot.id, spot.id]);
          }
        },
      );

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          response: {
            pairs: rsp,
          },
        },
      });

      setSelectedSpot(undefined);
    } else if (selectedSpot && selectedSpot.id === spot.id) {
      setSelectedSpot(undefined);
    } else {
      setSelectedSpot(spot);
    }
  };

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      const rsp: { pairs: [string?, string?][] } = { pairs: [] };

      if (props?.data?.scoring?.pairs) {
        props.data.scoring.pairs.forEach((p) => {
          if (!p.isKey) return;
          rsp.pairs.push([p.src, p.dst]);
        });
      }

      // todo: adjust mark for for shape type/size
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: rsp,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.revealKey]);

  // const handlePair = (pair) => {
  //   let rsp = produce(
  //     player?.response?.[props.data.id]?.pairs || [],
  //     (draft) => {
  //       let exists = false;
  //       draft.map((p) => {
  //         if (p.includes(pair[0]) && p.includes(pair[1])) {
  //           exists = true;
  //         }
  //       });
  //       if (!exists) {
  //         draft.push(pair);
  //       }
  //     }
  //   );

  //   dispatchPlayer({
  //     type: "RESPONSE",
  //     data: {
  //       id: props.data.id,
  //       response: {
  //         pairs: rsp,
  //       },
  //     },
  //   });
  // };

  const spots = produce(props.data.content.spots || [], (draft) => {
    draft?.forEach((spot) => {
      spot.props = { ...styles.normal };
      if (selectedSpot && selectedSpot.id === spot.id) {
        spot.props = { ...styles.selected };
      }
    });
  });

  return (
    <div className="item-player-element item-player-stem item-player-stem-graphic-associate">
      <Prompt data={props.data.content.prompt} />
      {props.data.content.image && (
        <ImageMap
          image={props.data.content.image}
          onSpotClick={handleSpotClick}
          map={{
            areas: spots,
          }}
        >
          {player.response?.[props.data.id]?.pairs?.map(
            (p: any, idx: number) => {
              const spot1 = find(props.data.content.spots, ["id", p[0]]);
              const spot2 = find(props.data.content.spots, ["id", p[1]]);

              let stroke = "#000";
              if (player.scoreResponses) {
                stroke = "#ff0000";

                props.data.scoring?.pairs?.forEach((rsp) => {
                  if (p.includes(rsp.src) && p.includes(rsp.dst)) {
                    stroke = "#00ff00";
                  }
                });
              }

              if (spot1 && spot2) {
                const scale = Math.min(
                  1,
                  650 / (props.data.content.image?.metadata.width ?? 0),
                );
                const spot1Center = getScaledCoordinates(
                  getHotspotCenter(spot1),
                  scale,
                );
                const spot2Center = getScaledCoordinates(
                  getHotspotCenter(spot2),
                  scale,
                );

                return (
                  <Line
                    key={idx}
                    points={[
                      spot1Center.x,
                      spot1Center.y,
                      spot2Center.x,
                      spot2Center.y,
                    ]}
                    stroke={stroke}
                    strokeWidth={2}
                  />
                );
              } else {
                return <React.Fragment key={idx}></React.Fragment>;
              }
            },
          )}
        </ImageMap>
      )}
    </div>
  );
};
export default GraphicAssociate;
